import React from "react";
import { Box, Drawer, IconButton, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotesPanel from "bundles/Internal/components/common/NotesPanel";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";

const C = {
  bg: "hsl(140 10% 95%)",
  border: "hsl(140 8% 88%)",
  text: "hsl(150 30% 12%)",
  muted: "hsl(150 10% 46%)",
  primary: "hsl(150 38% 17%)",
  primaryFg: "hsl(80 97% 72%)",
};

const RESOURCE_KIND = {
  candidates: "Candidate",
  recruiters: "Client",
  external_profiles: "Profile",
  companies: "Company",
  job_offers: "Job Offer",
  job_applications: "Job Application",
};

const defaultGetTitle = (record, resource) => {
  if (!record) return RESOURCE_KIND[resource] || "Record";
  const name = [record.first_name, record.last_name].filter(Boolean).join(" ");
  if (name) return name;
  if (record.job_title) return record.job_title;
  if (record.email) return record.email;
  if (record.name) return record.name;
  const label = RESOURCE_KIND[resource] || "Record";
  return record.id ? `${label} #${record.id}` : label;
};

const defaultGetSubtitle = (record, resource) => {
  if (!record) return RESOURCE_KIND[resource] || "Record";
  if (record.company_name) return record.company_name;
  if (record.email && (record.first_name || record.last_name || record.name)) {
    return record.email;
  }
  return RESOURCE_KIND[resource] || "Record";
};

const NotesDrawer = ({
  open,
  onClose,
  resource,
  record,
  getTitle,
  getSubtitle,
  onNoteAdded,
  onNoteArchived,
}) => {
  const recordId = record?.id;
  const title = (getTitle || defaultGetTitle)(record, resource);
  const subtitle = (getSubtitle || defaultGetSubtitle)(record, resource);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: 460 },
          backgroundColor: C.bg,
          borderLeft: `1px solid ${C.border}`,
          boxShadow: "-8px 0 32px rgba(16, 24, 40, 0.06)",
          display: "flex",
          flexDirection: "column",
          "&::-webkit-scrollbar": { width: 6, height: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "hsl(140 8% 82%)",
            borderRadius: 3,
            "&:hover": { background: C.muted },
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "#fff",
          borderBottom: `1px solid ${C.border}`,
          px: 2.5,
          py: 2,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <UserAvatar
            user={{ id: record?.id, full_name: title, picture_url: record?.picture_url }}
            size={36}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                color: C.text,
                fontSize: 14,
                lineHeight: 1.25,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                sx={{
                  color: C.muted,
                  fontSize: 12,
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Close notes drawer"
            disableRipple
            size="small"
            sx={{
              color: C.muted,
              borderRadius: "8px",
              "&:hover": {
                color: C.text,
                backgroundColor: "hsla(140, 8%, 90%, 0.6)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Box>

      {/* Content */}
      <Box
        sx={{
          p: 2,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <NotesPanel
          resource={resource}
          recordId={recordId}
          onNoteAdded={onNoteAdded}
          onNoteArchived={onNoteArchived}
        />
      </Box>
    </Drawer>
  );
};

export default NotesDrawer;
